/**
 * Workflow NP envío bodega por líneas de guía (D-028).
 *
 * Flujo: PDF → upload → `warehouseSendFromGuideLines` → `createWarehouseGuide`
 */

import { warehouseSendFromGuideLines } from '../../../../../slices/guideDetail/thunk';
import {
	readDTEFacele,
	createWarehouseGuide,
} from '../../../../../slices/warehouseGuide/thunk';
import {
	uploadFoldersFile,
	getFileTypeByCode,
} from '../../../../../slices/thunks';
import {
	waitForPDF,
	uploadDocument,
	generateFileName,
} from '../utils/pdfWorkflowUtils';
import { FOLDER_PATHS } from '../../constants/guideConstants';
import { mergeObservacionesGuiaForPayload } from '../../utils/residuosObservacionesAuto';
import {
	buildResiduosGuideDetailsFromFormItems,
	buildResiduosWarehouseGuidePayload,
} from '../../utils/buildResiduosWarehouseGuidePayload';
import { buildGuideLinesFromFormItems } from '../../utils/buildGuideLinesSendPayload';

export class ResiduosWarehouseGuideLinesSendWorkflow {
	static async execute(dispatch, dteResult, formValues, onProgress) {
		const dteId = dteResult?.data?._id;
		const folio = dteResult?.data?.folio || '';

		let documentId = null;
		let resGuideId = null;
		let warGuideId = null;

		try {
			if (onProgress) onProgress('wf_pdf', 'loading');

			const pdfBlob = await waitForPDF(dispatch, dteId, readDTEFacele);
			if (!pdfBlob) {
				if (onProgress)
					onProgress('wf_pdf', 'error', 'No se pudo obtener el PDF');
				const error = new Error(
					'No se pudo obtener el PDF de la guía consolidada.',
				);
				error.createdResources = {
					resGuideId,
					warGuideId,
					documentId,
					folio,
				};
				throw error;
			}
			if (onProgress) onProgress('wf_pdf', 'done');

			if (onProgress) onProgress('wf_upload', 'loading');
			const fileName = generateFileName(dteResult, 'RES-CONSOL');
			documentId = await uploadDocument(dispatch, pdfBlob, fileName, {
				establishmentId: formValues.establishment_id,
				folderPath:
					FOLDER_PATHS.RESIDUOS_CONSOLIDADAS ||
					'/Residuos/Guias/Consolidadas',
				getFileTypeByCodeThunk: getFileTypeByCode,
				uploadFoldersFileThunk: uploadFoldersFile,
			});

			if (!documentId) {
				if (onProgress)
					onProgress('wf_upload', 'error', 'Error al subir documento');
				const error = new Error(
					'No se pudo subir el documento de la guía consolidada.',
				);
				error.createdResources = {
					resGuideId,
					warGuideId,
					documentId,
					folio,
				};
				throw error;
			}
			if (onProgress) onProgress('wf_upload', 'done');

			if (onProgress) onProgress('wf_consolidate', 'loading');

			const guideDetails = buildGuideLinesFromFormItems(formValues.items);
			if (!guideDetails.length) {
				const error = new Error(
					'No hay líneas de guía seleccionadas para enviar.',
				);
				error.createdResources = {
					resGuideId,
					warGuideId,
					documentId,
					folio,
				};
				throw error;
			}

			const sendData = {
				data_guide: {
					dte_id: dteId,
					folio: parseInt(folio, 10),
					document_id: documentId,
					guide_date: formValues.guide_date,
					establishment_id: formValues.establishment_id,
					responsible_id: formValues.responsible_id,
					residue_type_id: formValues.residue_type_id,
					destiny_type_id: parseInt(formValues.destiny_type_id, 10),
					warehouse_id:
						formValues.destiny_type_id === '1'
							? formValues.warehouse_id
							: '',
					final_destination_id:
						formValues.destiny_type_id === '2'
							? formValues.final_destination_id
							: '',
					transfer_category_id: formValues.transfer_category_ids?.[0] || '',
					patente_id: formValues.patente_id,
					matricula_id: formValues.matricula_id || '',
					observation: mergeObservacionesGuiaForPayload(formValues),
				},
				data_management_guide_lines: {
					guide_details: guideDetails,
				},
			};

			try {
				const result = await dispatch(
					warehouseSendFromGuideLines(sendData),
				).unwrap();
				resGuideId = result.guide?._id;
				if (onProgress) onProgress('wf_consolidate', 'done');
			} catch (consolidateError) {
				if (onProgress)
					onProgress(
						'wf_consolidate',
						'error',
						consolidateError.message || 'Error al consolidar',
					);
				const error = new Error(
					consolidateError.message ||
						'Error al consolidar guías desde líneas',
				);
				error.createdResources = {
					resGuideId,
					warGuideId,
					documentId,
					folio,
				};
				throw error;
			}

			const warGuideDetails = buildResiduosGuideDetailsFromFormItems(
				formValues.items,
			);

			if (onProgress) onProgress('wf_warehouse', 'loading');
			const warehouseGuideData = buildResiduosWarehouseGuidePayload({
				dteId,
				folio,
				documentId,
				formValues,
				resGuideId,
				guideDetails: warGuideDetails,
			});

			try {
				const warehouseGuideResult = await dispatch(
					createWarehouseGuide(warehouseGuideData),
				).unwrap();
				warGuideId = warehouseGuideResult._id;
				if (onProgress) onProgress('wf_warehouse', 'done');
			} catch (whError) {
				console.error(
					'[ResiduosWarehouseGuideLinesSendWorkflow] Error createWarehouseGuide:',
					whError,
				);
				if (onProgress)
					onProgress(
						'wf_warehouse',
						'error',
						whError.message || 'Error al registrar en listado unificado',
					);
				const error = new Error(
					whError.message || 'Error al registrar en el listado unificado',
				);
				error.createdResources = {
					resGuideId,
					warGuideId,
					documentId,
					folio,
				};
				throw error;
			}

			return {
				success: true,
				guideId: resGuideId,
				folio,
				documentId,
				createdResources: {
					resGuideId,
					warGuideId,
					documentId,
					folio,
				},
			};
		} catch (error) {
			console.error(
				'[ResiduosWarehouseGuideLinesSendWorkflow] Error:',
				error,
			);
			if (!error.createdResources) {
				error.createdResources = {
					resGuideId,
					warGuideId,
					documentId,
					folio,
				};
			}
			throw error;
		}
	}
}

export default ResiduosWarehouseGuideLinesSendWorkflow;
